import { formatCurrency } from "@/lib/utils";
import { Edit2, Trash2, Package, Camera, Filter, Monitor, Battery, 
  Anchor, HardDrive, Wrench, Lightbulb, Grip, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { InventoryCard } from "./InventoryCard";
import { useTranslation } from 'react-i18next';
import { useCurrency } from "@/contexts/CurrencyContext";

const categoryIcons: Record<string, any> = {
  "Cameras": Camera,
  "Lenses": Camera,
  "Filters": Filter,
  "Monitoring": Monitor,
  "Power": Battery,
  "Camera Support": Anchor,
  "Media": HardDrive,
  "Camera AKS": Wrench,
  "Lighting Fixtures": Lightbulb,
  "Grip": Grip,
  "Specialty Items": Package,
};

interface InventoryListViewProps {
  items: any[];
  onEdit: (item: any) => void;
  onDelete: (id: string) => void;
  onAddItem?: () => void;
}

export function InventoryListView({ 
  items, 
  onEdit, 
  onDelete,
  onAddItem 
}: InventoryListViewProps) {
  const isMobile = useIsMobile();
  const { currency } = useCurrency();
  const isEmpty = items.length === 0;
  const { t } = useTranslation();

  return (
    <div className="bg-card/40 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden shadow-lg">
      <div className="p-3 sm:p-4 pb-3 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 bg-primary rounded-full"></div>
          <span className="text-sm font-semibold text-white/90">
            {t(isEmpty ? "no_items" : `items_count`, { count: items.length })}
          </span>
        </div>
      </div>
      
      {isMobile ? (
        // Mobile card view with better spacing
        <div className="px-3 pb-4">
          {isEmpty ? (
            <div className="py-16 flex flex-col items-center justify-center text-white/60">
              <div className="h-16 w-16 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                <Package className="h-8 w-8 text-primary/50" />
              </div>
              <p className="text-lg font-semibold mb-2 text-white">
                {t('your_inventory_is_empty')}
              </p>
              <p className="text-sm text-white/60 mb-6 text-center px-4 leading-relaxed max-w-xs">
                {t('add_first_item_to_track_equipment')}
              </p>
              {onAddItem && (
                <Button onClick={onAddItem} className="gap-2 h-12 px-6 text-sm font-semibold rounded-lg shadow-lg">
                  <Plus className="h-4 w-4" />
                  {t('add_your_first_item')}
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-3">
              {items.map((item) => (
                <InventoryCard 
                  key={item.id} 
                  item={item} 
                  onEdit={onEdit} 
                  onDelete={onDelete} 
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        // Desktop table view
        <Table>
          <TableHeader>
            <TableRow className="bg-black/30 border-b-0">
              <TableHead className="w-[300px] text-white/90 font-medium">
                {t('name')}
              </TableHead>
              <TableHead className="text-white/90 font-medium">
                {t('category')}
              </TableHead>
              <TableHead className="text-right text-white/90 font-medium">
                {t('quantity')}
              </TableHead>
              <TableHead className="text-right text-white/90 font-medium">
                {t('rate_per_day')}
              </TableHead>
              <TableHead className="text-right w-[100px] text-white/90 font-medium">
                {t('actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length > 0 ? (
              items.map((item) => {
                const IconComponent = categoryIcons[item.category] || Package;
                
                return (
                  <TableRow key={item.id} className="bg-transparent border-white/5 hover:bg-white/5 transition-colors">
                    <TableCell className="font-medium text-white">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 bg-primary/10 rounded-md flex items-center justify-center">
                          <IconComponent className="h-4 w-4 text-primary" />
                        </div>
                        <span className="truncate max-w-[200px]">{item.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-white/80">{item.category}</TableCell>
                    <TableCell className="text-right text-white/80 font-medium">{item.quantity}</TableCell>
                    <TableCell className="text-right text-white/80 font-medium">{formatCurrency(item.cost_per_unit, currency)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => onEdit(item)} className="h-8 w-8 hover:bg-white/10 rounded-full">
                          <Edit2 className="h-4 w-4 text-white/70" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => onDelete(item.id)} className="h-8 w-8 hover:bg-white/10 hover:text-destructive rounded-full">
                          <Trash2 className="h-4 w-4 text-white/70" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="h-64 text-center text-white/60">
                  <div className="flex flex-col items-center justify-center py-10">
                    <Package className="h-16 w-16 text-white/20 mb-4" />
                    <p className="text-xl font-medium mb-2">{t('your_inventory_is_empty')}</p>
                    <p className="text-sm text-white/50 mb-6 max-w-md mx-auto text-center">
                      {t('add_first_item_to_track_equipment_inventory')}
                    </p>
                    {onAddItem && (
                      <Button onClick={onAddItem} size="lg" className="gap-2">
                        <Plus className="h-4 w-4" />
                        {t('add_your_first_item')}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
